import { Capacitor } from '@capacitor/core';
import { Purchases } from '@revenuecat/purchases-capacitor';

// Product IDs for skins (these should match your RevenueCat dashboard setup)
export const PRODUCT_IDS = {
    SKIN_TRIANGLE: 'skin_triangle',
    SKIN_CIRCLE: 'skin_circle',
    SKIN_STAR: 'skin_star',
    SKIN_NEON: 'skin_neon',
    SKIN_FIRE: 'skin_fire',
    SKIN_ICE: 'skin_ice',
    SKIN_GOLD: 'skin_gold',
    REMOVE_ADS: 'com.balconygames.survival.remove_ads'
};

// Telegram Stars prices (in stars)
export const TELEGRAM_STAR_PRICES = {
    'triangle': 10,
    'circle': 10,
    'star': 20,
    'neon': 20,
    'fire': 30,
    'ice': 30,
    'gold': 50
};

// Map skin IDs to product IDs
const SKIN_TO_PRODUCT_MAP = {
    'triangle': PRODUCT_IDS.SKIN_TRIANGLE,
    'circle': PRODUCT_IDS.SKIN_CIRCLE,
    'star': PRODUCT_IDS.SKIN_STAR,
    'neon': PRODUCT_IDS.SKIN_NEON,
    'fire': PRODUCT_IDS.SKIN_FIRE,
    'ice': PRODUCT_IDS.SKIN_ICE,
    'gold': PRODUCT_IDS.SKIN_GOLD
};

// RevenueCat API keys (replace with your actual keys)
// Get these from https://app.revenuecat.com
const REVENUECAT_API_KEYS = {
    ios: 'appl_YOUR_IOS_API_KEY',
    android: 'goog_YOUR_ANDROID_API_KEY'
};

export class IAPManager {
    constructor(telegramIntegration = null) {
        this.isNative = Capacitor.isNativePlatform();
        this.isTelegram = typeof window !== 'undefined' && window.Telegram?.WebApp?.initData;
        this.telegram = telegramIntegration;
        this.initialized = false;
        this.offerings = null;
    }

    async initialize() {
        // Telegram mode - use Telegram Stars
        if (this.isTelegram) {
            console.log('IAP: Running in Telegram mode, using Telegram Stars');
            this.initialized = true;
            return true;
        }

        // Web mode
        if (!this.isNative) {
            console.log('IAP: Running in web mode, skipping native IAP initialization');
            this.initialized = true;
            return true;
        }

        try {
            // Configure RevenueCat
            const platform = Capacitor.getPlatform();
            const apiKey = platform === 'ios' ? REVENUECAT_API_KEYS.ios : REVENUECAT_API_KEYS.android;

            await Purchases.configure({
                apiKey: apiKey,
                appUserID: null // RevenueCat will generate anonymous ID
            });

            // Get available offerings
            const offerings = await Purchases.getOfferings();
            this.offerings = offerings;

            this.initialized = true;
            console.log('IAP: Initialized successfully with RevenueCat');

            return true;
        } catch (error) {
            console.error('IAP: Failed to initialize:', error);
            // Still mark as initialized to allow fallback behavior
            this.initialized = true;
            return false;
        }
    }

    async getPurchases() {
        if (!this.isNative) {
            // Web mode: get from localStorage
            const stored = localStorage.getItem('iap_purchases');
            return stored ? JSON.parse(stored) : [];
        }

        try {
            const purchaserInfo = await Purchases.getCustomerInfo();
            const activePurchases = [];

            // Check which skins are unlocked
            for (const [skinId, productId] of Object.entries(SKIN_TO_PRODUCT_MAP)) {
                if (purchaserInfo.customerInfo.entitlements.active[productId] ||
                    purchaserInfo.customerInfo.activeSubscriptions.includes(productId)) {
                    activePurchases.push(skinId);
                }
            }

            // Also check localStorage for web purchases
            const localPurchases = localStorage.getItem('iap_purchases');
            const local = localPurchases ? JSON.parse(localPurchases) : [];

            // Merge and deduplicate
            const merged = [...new Set([...activePurchases, ...local])];

            // Update localStorage
            localStorage.setItem('iap_purchases', JSON.stringify(merged));

            return merged;
        } catch (error) {
            console.error('IAP: Failed to get purchases:', error);
            // Fallback to localStorage
            const stored = localStorage.getItem('iap_purchases');
            return stored ? JSON.parse(stored) : [];
        }
    }

    async hasPurchased(skinId) {
        const purchases = await this.getPurchases();
        return purchases.includes(skinId);
    }

    async restorePurchases() {
        if (!this.isNative) {
            console.log('IAP: Restore not available in web mode');
            return [];
        }

        try {
            const purchaserInfo = await Purchases.restorePurchases();

            // Sync purchases to localStorage
            await this.getPurchases();

            console.log('IAP: Purchases restored successfully');
            return purchaserInfo;
        } catch (error) {
            console.error('IAP: Failed to restore purchases:', error);
            throw error;
        }
    }

    async purchaseSkin(skinId, onSuccess, onError) {
        try {
            // Check if already purchased
            if (await this.hasPurchased(skinId)) {
                if (onError) onError('Already purchased');
                return;
            }

            // Get product ID
            const productId = SKIN_TO_PRODUCT_MAP[skinId];
            if (!productId) {
                if (onError) onError('Invalid skin');
                return;
            }

            // Telegram Stars payment
            if (this.isTelegram) {
                const price = TELEGRAM_STAR_PRICES[skinId];
                if (!price) {
                    if (onError) onError('Invalid skin');
                    return;
                }

                try {
                    // Create invoice link using Telegram bot
                    const userId = window.Telegram.WebApp.initDataUnsafe?.user?.id;
                    if (!userId) {
                        if (onError) onError('Unable to identify user');
                        return;
                    }

                    // Send purchase request to bot
                    const invoiceLink = await this.createTelegramInvoice(skinId, price, userId);

                    // Open invoice in Telegram
                    window.Telegram.WebApp.openInvoice(invoiceLink, (status) => {
                        if (status === 'paid') {
                            this.storePurchase(skinId);
                            if (onSuccess) onSuccess();
                        } else if (status === 'cancelled') {
                            if (onError) onError('Payment cancelled');
                        } else {
                            if (onError) onError('Payment failed');
                        }
                    });
                    return;
                } catch (error) {
                    console.error('Telegram payment error:', error);
                    if (onError) onError('Payment failed: ' + error.message);
                    return;
                }
            }

            // Web fallback (for testing or web-only features)
            if (!this.isNative) {
                const confirmed = confirm(`Purchase ${skinId} skin?\n\nThis is a simulation in web mode.`);
                if (confirmed) {
                    this.storePurchase(skinId);
                    if (onSuccess) onSuccess();
                } else {
                    if (onError) onError('User cancelled');
                }
                return;
            }

            // Native IAP purchase using RevenueCat
            try {
                // Get the package for this product
                const purchaserInfo = await Purchases.purchaseStoreProduct({
                    product: productId
                });

                // Check if purchase was successful
                if (purchaserInfo.customerInfo.entitlements.active[productId]) {
                    this.storePurchase(skinId);
                    if (onSuccess) onSuccess();
                } else {
                    if (onError) onError('Purchase verification failed');
                }
            } catch (error) {
                console.error('IAP: Purchase failed:', error);

                // Check if user cancelled
                if (error.userCancelled) {
                    if (onError) onError('User cancelled');
                } else {
                    if (onError) onError(error.message || 'Purchase failed');
                }
            }
        } catch (error) {
            console.error('IAP: Error in purchaseSkin:', error);
            if (onError) onError(error.message || 'Purchase error');
        }
    }

    storePurchase(skinId) {
        const purchases = JSON.parse(localStorage.getItem('iap_purchases') || '[]');
        if (!purchases.includes(skinId)) {
            purchases.push(skinId);
            localStorage.setItem('iap_purchases', JSON.stringify(purchases));
        }
    }

    async createTelegramInvoice(skinId, price, userId) {
        // API endpoint of your Telegram bot
        const BOT_API_URL = process.env.TELEGRAM_BOT_API_URL || 'https://your-bot-api.com/create-invoice';

        const response = await fetch(BOT_API_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                skinId,
                price,
                userId
            })
        });

        if (!response.ok) {
            throw new Error('Failed to create invoice');
        }

        const data = await response.json();
        return data.invoiceLink;
    }

    async getFormattedPrice(skinId) {
        // Telegram Stars pricing
        if (this.isTelegram) {
            const stars = TELEGRAM_STAR_PRICES[skinId];
            return stars ? `⭐ ${stars}` : '⭐ 10';
        }

        // Default prices for web or if products aren't loaded
        const defaultPrices = {
            'triangle': '$0.99',
            'circle': '$0.99',
            'star': '$1.99',
            'neon': '$1.99',
            'fire': '$2.99',
            'ice': '$2.99',
            'gold': '$3.99'
        };

        if (!this.isNative) {
            return defaultPrices[skinId] || '$0.99';
        }

        try {
            const productId = SKIN_TO_PRODUCT_MAP[skinId];
            if (!productId) return defaultPrices[skinId] || '$0.99';

            // Get product details
            const products = await Purchases.getProducts({
                productIdentifiers: [productId]
            });

            if (products.products && products.products.length > 0) {
                const product = products.products[0];
                return product.priceString || defaultPrices[skinId];
            }

            return defaultPrices[skinId] || '$0.99';
        } catch (error) {
            console.error('IAP: Failed to get price:', error);
            return defaultPrices[skinId] || '$0.99';
        }
    }

    // Set user ID (optional, for tracking purchases across devices)
    async setUserID(userID) {
        if (!this.isNative) return;

        try {
            await Purchases.logIn({ appUserID: userID });
            console.log('IAP: User logged in:', userID);
        } catch (error) {
            console.error('IAP: Failed to set user ID:', error);
        }
    }

    async purchaseSpaceship(spaceshipId, stars) {
        try {
            // Telegram Stars payment
            if (this.isTelegram) {
                const userId = window.Telegram.WebApp.initDataUnsafe?.user?.id;
                if (!userId) {
                    console.error('Unable to identify user');
                    return false;
                }

                // Create invoice for spaceship
                const BOT_API_URL = process.env.TELEGRAM_BOT_API_URL || 'https://your-bot-api.com/create-invoice';
                const response = await fetch(BOT_API_URL, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        itemId: `spaceship_${spaceshipId}`,
                        itemName: spaceshipId,
                        price: stars,
                        userId
                    })
                });

                if (!response.ok) {
                    throw new Error('Failed to create invoice');
                }

                const data = await response.json();

                return new Promise((resolve) => {
                    window.Telegram.WebApp.openInvoice(data.invoiceLink, (status) => {
                        if (status === 'paid') {
                            // Store spaceship purchase
                            const purchases = JSON.parse(localStorage.getItem('spaceship_purchases') || '[]');
                            if (!purchases.includes(spaceshipId)) {
                                purchases.push(spaceshipId);
                                localStorage.setItem('spaceship_purchases', JSON.stringify(purchases));
                            }
                            resolve(true);
                        } else {
                            resolve(false);
                        }
                    });
                });
            }

            // Web fallback
            const confirmed = confirm(`Purchase ${spaceshipId} spaceship for ${stars} ⭐?\n\nThis is a simulation in web mode.`);
            if (confirmed) {
                const purchases = JSON.parse(localStorage.getItem('spaceship_purchases') || '[]');
                if (!purchases.includes(spaceshipId)) {
                    purchases.push(spaceshipId);
                    localStorage.setItem('spaceship_purchases', JSON.stringify(purchases));
                }
                return true;
            }
            return false;
        } catch (error) {
            console.error('Spaceship purchase error:', error);
            return false;
        }
    }

    // -------------------------------------------------------------------------
    // Remove Ads
    // -------------------------------------------------------------------------

    async hasRemovedAds() {
        if (!this.isNative) {
            return localStorage.getItem('remove_ads') === '1';
        }

        try {
            const purchaserInfo = await Purchases.getCustomerInfo();
            const active = purchaserInfo.customerInfo.entitlements.active;
            if (active[PRODUCT_IDS.REMOVE_ADS]) return true;
        } catch (_) { /* fall through */ }

        return localStorage.getItem('remove_ads') === '1';
    }

    async purchaseRemoveAds(onSuccess, onError) {
        if (await this.hasRemovedAds()) {
            if (onSuccess) onSuccess();
            return;
        }

        // Web / Telegram fallback
        if (!this.isNative) {
            const confirmed = confirm('Remove all ads? (web simulation)');
            if (confirmed) {
                localStorage.setItem('remove_ads', '1');
                if (onSuccess) onSuccess();
            } else {
                if (onError) onError('User cancelled');
            }
            return;
        }

        try {
            const purchaserInfo = await Purchases.purchaseStoreProduct({
                product: PRODUCT_IDS.REMOVE_ADS
            });

            if (purchaserInfo.customerInfo.entitlements.active[PRODUCT_IDS.REMOVE_ADS]) {
                localStorage.setItem('remove_ads', '1');
                if (onSuccess) onSuccess();
            } else {
                if (onError) onError('Purchase verification failed');
            }
        } catch (error) {
            if (error.userCancelled) {
                if (onError) onError('User cancelled');
            } else {
                if (onError) onError(error.message || 'Purchase failed');
            }
        }
    }

    async restoreRemoveAds() {
        if (!this.isNative) return false;

        try {
            await Purchases.restorePurchases();
            return await this.hasRemovedAds();
        } catch (error) {
            console.error('IAP: restore remove_ads failed:', error);
            return false;
        }
    }

    // Clean up
    destroy() {
        // RevenueCat handles cleanup automatically
    }
}

// Export singleton instance
export const iapManager = new IAPManager();
